import { scenarioDetails, type ScenarioDetail } from '@/data/scenarioDetails';
import { apiPreferredKoEn } from '@/lib/i18nLocale';
import type { Scenario } from '@/types/admin';
import type { GetScenarioBasicInfoResponseDto, ScenarioAdminDto } from '@/types/api/scenario';
import { pickHandoverTextForLocale } from '@/utils/scenarioUtils';

/** "7분" 등 duration 문자열에서 분 숫자만 뽑기 (목록/시트 보조) */
function minutesFromDurationLabel(label: string | undefined): number {
	if (!label) return 0;
	const m = String(label).match(/(\d+)\s*분/);
	return m ? parseInt(m[1], 10) : 0;
}

function strFromUnknown(u: unknown): string {
	if (u == null) return '';
	if (typeof u === 'string') return u.trim();
	if (typeof u === 'number' && Number.isFinite(u)) return String(u);
	if (typeof u === 'object' && !Array.isArray(u)) {
		const r = u as Record<string, unknown>;
		for (const key of ['value', 'title', 'name', 'label', 'ko', 'en', 'en-US', 'en_US'] as const) {
			const v = r[key];
			if (typeof v === 'string' && v.trim()) return v.trim();
			if (typeof v === 'number' && Number.isFinite(v)) return String(v);
		}
		for (const v of Object.values(r)) {
			if (typeof v === 'string' && v.trim()) return v.trim();
		}
	}
	return '';
}

function deviceNamesToPlatform(deviceNames: string[] | undefined): 'VR' | 'Mobile' | 'PC' {
	const s = (deviceNames ?? []).join(' ').toUpperCase();
	if (s.includes('VR') || s.includes('QUEST') || s.includes('META')) return 'VR';
	if (s.includes('MOBILE')) return 'Mobile';
	if (s.includes('PC') || s.includes('WEB') || s.includes('WINDOWS')) return 'PC';
	return 'VR';
}

/** /scenario/{id}/info 예상시간(초) */
function secondsFromBasicInfo(info: GetScenarioBasicInfoResponseDto | null | undefined): number {
	if (!info) return 0;
	const seconds = Number(info.expectedPlayTimeSeconds);
	if (Number.isFinite(seconds) && seconds > 0) return Math.trunc(seconds);
	return 0;
}

function categoryFromDtoInfoAndResolver(
	dto: ScenarioAdminDto,
	info: GetScenarioBasicInfoResponseDto | null | undefined,
	fromSheet: Partial<ScenarioDetail> | undefined,
	categoryNameById?: (id: number) => string | undefined
): string {
	if (dto.scenarioCategoryTitle && dto.scenarioCategoryTitle.trim()) {
		return dto.scenarioCategoryTitle.trim();
	}
	const fromList = (dto.scenarioCategoryList ?? [])
		.map((c) => (c?.title && String(c.title).trim() ? String(c.title).trim() : ''))
		.filter(Boolean);
	if (fromList.length > 0) {
		if (fromList.length === 1) return fromList[0]!;
		return fromList.join(' · ');
	}
	if (fromSheet?.category && String(fromSheet.category).trim()) {
		return String(fromSheet.category).trim();
	}
	if (info?.scenarioCategoryId && categoryNameById) {
		const id = Number(info.scenarioCategoryId);
		if (Number.isFinite(id) && id > 0) {
			const t = categoryNameById(id);
			if (t) return t;
		}
	}
	return '—';
}

function authorFromBasicInfo(
	dto: ScenarioAdminDto,
	info: GetScenarioBasicInfoResponseDto | null | undefined,
	fromSheet: Partial<ScenarioDetail> | undefined
): string {
	const a = (dto.contributedBy && String(dto.contributedBy).trim());
	if (a) return a;
	if (fromSheet?.ContributedBy) return fromSheet.ContributedBy;
	return '—';
}

function hasHangul(s: string): boolean {
	return /[\uAC00-\uD7A3]/.test(s);
}

/**
 * API가 `language=ko`로도 handoverNote를 영어만 줄 때(국문 미등록) — 제목은 한·본문은 영어로 "갑자기 영어"처럼 보이는 케이스
 * 한국어 UI에서는 긴 **영어 전용** 본문은 목록 미리보기에서 제외(확인 후 상세/저장 흐름의 info 는 그대로)
 */
function handoverListPreviewFromNote(
	note: string,
	uiLanguage: string | undefined
): string {
	const t = note.trim();
	if (!t) return '';
	if (uiLanguage == null || apiPreferredKoEn(uiLanguage) !== 'ko') return t;
	/** 한·중·일 문자가 있으면 그대로(혼용 포함) */
	if (hasHangul(t) || /[\u3040-\u30FF\u4E00-\u9FFF]/.test(t)) return t;
	/** 짧은 라벨은 그대로 */
	if (t.length < 32) return t;
	/** 긴 영어 전용(임상 서술 등) */
	if (t.length >= 32 && /[A-Za-z]/.test(t) && !hasHangul(t)) return '';
	return t;
}

function platformFromFreeText(raw: string | undefined | null): 'VR' | 'Mobile' | 'PC' | null {
	if (raw == null) return null;
	const s = String(raw).toUpperCase();
	if (s.includes('PC') || s.includes('WEB') || s.includes('WINDOWS')) return 'PC';
	if (s.includes('MOBILE') || s.includes('IOS') || s.includes('ANDROID')) return 'Mobile';
	if (s.includes('VR') || s.includes('QUEST') || s.includes('OCULUS')) return 'VR';
	return null;
}

/**
 * 백엔드 목록 DTO(+선택: 기본 info) → 커리큘럼/세션 리스트에 쓰는 `ScenarioDetail` (ContributedBy·duration·handover 정합)
 * @param uiLocale `i18n.language` — `handoverNote`가 `{ ko, en, ... }`일 때
 */
export function mapScenarioAdminDtoToCurriculumScenarioDetail(
	dto: ScenarioAdminDto,
	basicInfo: GetScenarioBasicInfoResponseDto | null | undefined,
	uiLocale?: string
): ScenarioDetail {
	const fromSheet = scenarioDetails[dto.scenarioId] as Partial<ScenarioDetail> | undefined;

	const secD = typeof dto.expectedPlayTimeSeconds === 'number' && dto.expectedPlayTimeSeconds > 0
		? Math.trunc(dto.expectedPlayTimeSeconds)
		: 0;
	const fromInfo = secondsFromBasicInfo(basicInfo);
	const totalSec = secD > 0 ? secD : fromInfo;

	const handoverFromInfo =
		basicInfo != null ? pickHandoverTextForLocale(basicInfo.handoverNote, uiLocale).trim() : '';
	const handover = (handoverFromInfo || fromSheet?.handover || '').trim();

	const author = authorFromBasicInfo(dto, basicInfo, fromSheet);

	const sheetMinutes = fromSheet?.duration != null ? minutesFromDurationLabel(fromSheet.duration) : 0;
	const minFromSec = totalSec > 0 ? Math.max(1, Math.round(totalSec / 60)) : 0;
	const displayMinutes = minFromSec > 0 ? minFromSec : sheetMinutes > 0 ? sheetMinutes : 0;
	const durationLabel = displayMinutes > 0 ? `${displayMinutes}분` : '0분';

	const platform = (fromSheet?.platform ?? deviceNamesToPlatform(dto.deviceNames)) as 'VR' | 'Mobile' | 'PC';

	if (fromSheet) {
		return {
			...fromSheet,
			id: dto.scenarioId,
			title: dto.title || fromSheet.title,
			duration: durationLabel,
			...(totalSec > 0 ? { expectedPlayTimeSeconds: totalSec } : {}),
			ContributedBy: author,
			handover: handover || (fromSheet.handover ?? ''),
			platform: (platform || fromSheet.platform) as 'VR' | 'Mobile' | 'PC',
			version: dto.version || fromSheet.version,
		} as ScenarioDetail;
	}

	const min: Scenario = {
		id: dto.scenarioId,
		classId: '',
		title: dto.title,
		learningObjectives: [],
		duration: durationLabel,
		supportedDevices: [],
		videoUrl: '',
		handover: handover || ' ',
		keyTasks: [],
		simulationExamples: [],
		createdDate: (dto.createdAt && String(dto.createdAt).slice(0, 10)) || '',
		updatedDate: (dto.createdAt && String(dto.createdAt).slice(0, 10)) || '',
		ContributedBy: author,
		category: dto.scenarioCategoryList?.[0]?.title || '',
		views: dto.views ?? 0,
		simulationCount: dto.simulationCount ?? 0,
		userCount: dto.userCount ?? 0,
		status: dto.status === 'inactive' ? 'inactive' : 'active',
		isPublic: true,
		version: dto.version || '1.0.0',
		averageDuration: durationLabel,
		averageScore: 0,
		platform,
	};

	return (totalSec > 0
		? { ...min, expectedPlayTimeSeconds: totalSec }
		: min) as unknown as ScenarioDetail;
}

const HANDOVER_PREVIEW = 200;
const HO_EXTENSION_SEP = '---\n[관리자 확장 정보]';

/** 제목 밑 본문 — `handoverNote`가 다국어 객체인 경우 `uiLanguage`에 맞는 슬롯 */
function handoverUnderTitlePreview(
	info: GetScenarioBasicInfoResponseDto | null | undefined,
	fromSheet: Partial<ScenarioDetail> | undefined,
	uiLanguage: string | undefined
): string {
	const fromInfo =
		info != null
			? pickHandoverTextForLocale((info as { handoverNote?: unknown }).handoverNote, uiLanguage)
			: '';
	const fromS = (fromSheet?.handover && String(fromSheet.handover)) || '';
	let body = (fromInfo || fromS).trim();
	if (!body) return '';
	const extIdx = body.indexOf(HO_EXTENSION_SEP);
	if (extIdx >= 0) body = body.slice(0, extIdx).trim();
	if (body.length > HANDOVER_PREVIEW) {
		return body.slice(0, HANDOVER_PREVIEW) + '…';
	}
	return body;
}

/**
 * 시나리오 선택 모달 한 줄(제목·요약·카테고리·저자·N분·플랫폼)
 * `uiLanguage`: i18n.language (한국 UI에서 영어 전용 handoverNote 미리보기 숨김)
 * `options.categoryNameById`: /scenario-type/list id → 제목 (info에 categoryId만 있을 때)
 */
export function getScenarioAdminListRowDisplay(
	dto: ScenarioAdminDto,
	info?: GetScenarioBasicInfoResponseDto | null,
	uiLanguage?: string,
	options?: { categoryNameById?: (id: number) => string | undefined }
): {
	categoryPart: string;
	author: string;
	/** @deprecated 모달은 handoverUnderTitle 사용 — 검색 등 한글 UI 영문 숨김용으로 유지 */
	descriptionLine: string;
	/** 제목 하단 본문 미리보기(handover) */
	handoverUnderTitle: string;
	minutesPart: string;
	platform: 'VR' | 'Mobile' | 'PC';
} {
	const fromSheet = scenarioDetails[dto.scenarioId];
	const minFromList =
		typeof dto.expectedPlayTimeSeconds === 'number' && dto.expectedPlayTimeSeconds > 0
			? Math.max(1, Math.round(dto.expectedPlayTimeSeconds / 60))
			: 0;
	const sInf = secondsFromBasicInfo(info ?? null);
	const minFromInfo = sInf > 0 ? Math.max(1, Math.round(sInf / 60)) : 0;
	const minFromSheet = fromSheet ? minutesFromDurationLabel(fromSheet.duration) : 0;
	const m = minFromList > 0 ? minFromList : (minFromInfo > 0 ? minFromInfo : minFromSheet);
	const author = authorFromBasicInfo(dto, info ?? null, fromSheet);
	let line = handoverListPreviewFromNote(
		(info && pickHandoverTextForLocale((info as { handoverNote?: unknown }).handoverNote, uiLanguage)) ||
			(fromSheet?.handover && String(fromSheet.handover)) ||
			'',
		uiLanguage
	);
	if (line.length > HANDOVER_PREVIEW) {
		line = line.slice(0, HANDOVER_PREVIEW) + '…';
	}
	const minutesPart = m > 0 ? `${m}분` : '—';
	const platformFromInfo =
		info?.deviceModel ? platformFromFreeText(strFromUnknown(info.deviceModel)) : null;
	const basePlatform: 'VR' | 'Mobile' | 'PC' =
		(fromSheet?.platform as 'VR' | 'Mobile' | 'PC' | undefined) ||
		platformFromInfo ||
		deviceNamesToPlatform(dto.deviceNames);
	const categoryPart = categoryFromDtoInfoAndResolver(dto, info ?? null, fromSheet, options?.categoryNameById);
	const handoverUnderTitle = handoverUnderTitlePreview(info ?? null, fromSheet, uiLanguage);
	return {
		categoryPart,
		author,
		descriptionLine: line,
		handoverUnderTitle,
		minutesPart,
		platform: basePlatform,
	};
}
